import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import MetricsCard from './components/MetricsCard';
import FilterControls from './components/FilterControls';
import TransactionVolumeChart from './components/TransactionVolumeChart';
import StakeholderPerformanceChart from './components/StakeholderPerformanceChart';
import ComplianceTracker from './components/ComplianceTracker';
import ReportGenerator from './components/ReportGenerator';
import AuditLogExporter from './components/AuditLogExporter';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ReportsAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    dateRange: 'last30days',
    transactionType: 'all',
    stakeholderRole: 'all',
    metricType: 'completion',
    startDate: '',
    endDate: ''
  });

  // Mock data for metrics cards
  const metricsData = [
    {
      title: "Transaction Completion Rate",
      value: "87.3%",
      change: "+5.2%",
      changeType: "positive",
      icon: "TrendingUp",
      description: "Average completion rate across all transaction types",
      trend: [65, 70, 75, 82, 85, 87, 89, 87]
    },
    {
      title: "Average TRL Progression Time",
      value: "14.2 days",
      change: "-2.1 days",
      changeType: "positive",
      icon: "Clock",
      description: "Time to reach Transaction Readiness Level 5",
      trend: [18, 17, 16, 15, 14, 14, 15, 14]
    },
    {
      title: "Document Processing Efficiency",
      value: "94.7%",
      change: "+1.8%",
      changeType: "positive",
      icon: "FileCheck",
      description: "Documents processed without errors or delays",
      trend: [90, 91, 93, 94, 95, 94, 95, 95]
    },
    {
      title: "Stakeholder Response Time",
      value: "2.3 hours",
      change: "-0.7 hours",
      changeType: "positive",
      icon: "Users",
      description: "Average response time for document reviews",
      trend: [4, 3.5, 3, 2.8, 2.5, 2.3, 2.4, 2.3]
    }
  ];

  // Mock data for transaction volume chart
  const transactionVolumeData = [
    { month: 'Jan', completed: 45, inProgress: 23, initiated: 67 },
    { month: 'Feb', completed: 52, inProgress: 28, initiated: 73 },
    { month: 'Mar', completed: 48, inProgress: 31, initiated: 69 },
    { month: 'Apr', completed: 61, inProgress: 25, initiated: 82 },
    { month: 'May', completed: 55, inProgress: 29, initiated: 78 },
    { month: 'Jun', completed: 67, inProgress: 22, initiated: 85 },
    { month: 'Jul', completed: 59, inProgress: 27, initiated: 81 },
    { month: 'Aug', completed: 72, inProgress: 24, initiated: 89 },
    { month: 'Sep', completed: 68, inProgress: 26, initiated: 87 }
  ];

  // Mock data for stakeholder performance chart
  const stakeholderPerformanceData = [
    { stakeholder: 'Lawyers', efficiency: 92, quality: 96, timeliness: 88 },
    { stakeholder: 'Engineers', efficiency: 89, quality: 94, timeliness: 91 },
    { stakeholder: 'Notaries', efficiency: 95, quality: 98, timeliness: 93 },
    { stakeholder: 'Property Owners', efficiency: 78, quality: 85, timeliness: 82 },
    { stakeholder: 'Administrators', efficiency: 97, quality: 99, timeliness: 95 }
  ];

  // Mock data for compliance tracking
  const complianceData = [
    {
      milestone: "Property Title Verification",
      description: "Verify clear title and ownership documentation",
      status: "completed",
      dueDate: "2025-09-15",
      completedDate: "2025-09-14",
      assignedTo: "Legal Team"
    },
    {
      milestone: "Engineering Assessment Report",
      description: "Complete structural and environmental assessment",
      status: "completed",
      dueDate: "2025-09-18",
      completedDate: "2025-09-17",
      assignedTo: "Engineering Team"
    },
    {
      milestone: "Financial Documentation Review",
      description: "Review and validate all financial documents",
      status: "in-progress",
      dueDate: "2025-09-25",
      assignedTo: "Finance Team"
    },
    {
      milestone: "Regulatory Compliance Check",
      description: "Ensure compliance with local regulations",
      status: "pending",
      dueDate: "2025-09-30",
      assignedTo: "Compliance Officer"
    },
    {
      milestone: "Digital Signature Collection",
      description: "Collect all required digital signatures",
      status: "overdue",
      dueDate: "2025-09-20",
      assignedTo: "Transaction Manager"
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'reports', label: 'Report Generator', icon: 'FileText' },
    { id: 'audit', label: 'Audit Logs', icon: 'Shield' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    console.log('Applying filters:', filters);
    // Implement filter application logic
  };

  const handleResetFilters = () => {
    setFilters({
      dateRange: 'last30days',
      transactionType: 'all',
      stakeholderRole: 'all',
      metricType: 'completion',
      startDate: '',
      endDate: ''
    });
  };

  const handleExportReport = (format) => {
    console.log(`Exporting report in ${format} format`);
    // Implement export functionality
  };

  const handleGenerateReport = (config) => {
    console.log('Generating report with config:', config);
    // Implement report generation
  };

  const handleScheduleReport = (config) => {
    console.log('Scheduling report with config:', config);
    // Implement report scheduling
  };

  const handleExportAuditLog = (config) => {
    console.log('Exporting audit log with config:', config);
    // Implement audit log export
  };

  useEffect(() => {
    // Simulate real-time data updates
    const interval = setInterval(() => {
      // Update metrics or refresh data
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Reports & Analytics - PropertyChain</title>
        <meta name="description" content="Comprehensive transaction performance insights and compliance reporting for PropertyChain stakeholders" />
      </Helmet>
      <Header />
      <Breadcrumbs />
      <main className="pt-32 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Reports & Analytics</h1>
              <p className="text-muted-foreground">
                Comprehensive transaction performance insights and compliance reporting
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => window.location?.reload()}
              >
                Refresh Data
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                onClick={() => handleExportReport('pdf')}
              >
                Quick Export
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center space-x-1 mb-8 bg-muted p-1 rounded-lg w-fit">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                  activeTab === tab?.id
                    ? 'bg-card text-primary shadow-soft'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Filter Controls */}
              <FilterControls
                filters={filters}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                onExportReport={handleExportReport}
              />

              {/* Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {metricsData?.map((metric, index) => (
                  <MetricsCard
                    key={index}
                    title={metric?.title}
                    value={metric?.value}
                    change={metric?.change}
                    changeType={metric?.changeType}
                    icon={metric?.icon}
                    description={metric?.description}
                    trend={metric?.trend}
                  />
                ))}
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <TransactionVolumeChart data={transactionVolumeData} />
                <StakeholderPerformanceChart data={stakeholderPerformanceData} />
              </div>

              {/* Compliance Tracker */}
              <ComplianceTracker complianceData={complianceData} />
            </div>
          )}

          {activeTab === 'reports' && (
            <ReportGenerator
              onGenerateReport={handleGenerateReport}
              onScheduleReport={handleScheduleReport}
            />
          )}

          {activeTab === 'audit' && (
            <AuditLogExporter onExportAuditLog={handleExportAuditLog} />
          )}
        </div>
      </main>
    </div>
  );
};

export default ReportsAnalytics;